/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-runloop, ember/require-computed-property-dependencies, getter-return */
// Extends from
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { action, computed } from '@ember/object';
import { filterBy } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import { later } from '@ember/runloop';
import titleize from 'ember-cli-string-helpers/utils/titleize';
// External Imports
import { tablePrintCss } from 'linced-app/utils/table';
import {
  cleanExcelSheetName,
  cleanFileName,
  uniqueSheetName,
} from '@linc-technologies/linced-common/utils/file-names';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';

export default class TeamRollController extends Controller {
  queryParams = ['teamId', 'reliever', 'weekly', 'date', 'fullTeam'];

  // Services
  @service
  dates;

  @service
  excel;

  @service
  options;

  @service
  session;

  @service
  store;

  @optionAlias('core:groups:teams')
  allTeams;

  @filterBy('allTeams', 'value')
  cleanTeams;

  @tracked teamId = null;
  @tracked reliever = false;
  @tracked weekly = false;
  @tracked fullTeam = false;
  @tracked date = moment().format('YYYY-MM-DD');
  minDate = moment().format('YYYY-01-01');
  maxDate = moment().format('YYYY-12-31');

  @computed('teamId', 'cleanTeams.[]')
  get team() {
    let team = this.cleanTeams.find((item) => item.value === this.teamId);
    return !isEmpty(team) ? team : null;
  }

  get teams() {
    if (this.fullTeam) {
      return this.cleanTeams;
    }

    if (!isEmpty(this.teamId)) {
      return [this.team];
    }
  }

  @computed('model.groups.@each.team')
  get learnerTeamMap() {
    return this.model.groups
      .filter((group) => group.team)
      .reduce((teamMap, group) => {
        let groupLearners = group
          .get('currentLearners')
          .filter(
            (learner) =>
              learner.get('person.enrolmentStatus') === EnrolmentStatus.CURRENT,
          );

        if (groupLearners.length) {
          if (isEmpty(teamMap[group.team])) {
            teamMap[group.team] = [];
          }
          teamMap[group.team].push(...groupLearners);
        }

        return teamMap;
      }, {});
  }

  @computed('team', 'reliever', 'weekly', 'class')
  get title() {
    if (!isEmpty(this.team)) {
      return `${this.team.label} ${
        this.reliever ? 'relievers' : `${this.weekly ? 'weekly' : 'daily'}`
      } team list`;
    }

    return `${
      this.reliever ? 'Reliever' : 'Blank'
    } team list (${this.weekly ? 'weekly' : 'daily'})`;
  }

  get schoolOpen() {
    let date = this.model.dates.find((item) => item.date === this.date);
    return !isEmpty(date) && date.status !== SchoolStatus.CLOSED;
  }

  // sortDir provides the current direction of sorting.
  @tracked
  sortDir = 'asc';

  // sortProp provides the current entity to be used to sort by.
  @tracked
  sortProp = 'lastName';

  @action
  onChangeDate(value) {
    // Restrict to current year.
    if (moment(value).year() === moment().year()) {
      this.date = value;
    }
  }

  @action
  downloadFullXlsx() {
    let sheets = [];
    let sheetNames = [];

    this.teams.forEach((team) => {
      let sheetName = uniqueSheetName(
        cleanExcelSheetName(team.get('label')),
        sheetNames,
      );
      sheetNames.push(sheetName);

      sheets.push({
        name: sheetName,
        data: this.getExportData(team),
      });
    });

    let fileName = `${this.title} ${moment().format('YYYY-MM-DD')}.xlsx`;

    this.excel.export(sheets, {
      multiSheet: true,
      fileName: cleanFileName(fileName),
    });
  }

  @action
  print() {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );
    let classLists = document.querySelectorAll('.class-list');
    let content = tablePrintCss;

    // CSS for displaying underlines
    content += `<style>
                  [class*="__homeroom-date"] {
                    border-bottom: 1px solid black;
                    display: inline-block;
                    margin-right: 20px;
                    width: 100px;
                  }
                  [class*="__homeroom-teachers-name"] {
                    border-bottom: 1px solid black;
                    display: inline-block;
                    margin-right: 20px;
                    width: 200px;
                  }
                  [class*="__signature"] {
                    border-bottom: 1px solid black;
                    display: inline-block;
                    width: 200px;
                  }
                </style>`;

    content += `<style>.table-tools button { display: none; }</style>`;

    classLists.forEach((classList, index) => {
      if (index !== 0) {
        content += `<div style="page-break-before: always"></div>`;
      }

      content += classList.outerHTML;
    });

    printer.document.body.innerHTML = content;

    later(
      this,
      (printer) => {
        printer.print();
        printer.close();
      },
      printer,
      500,
    );
  }

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  getExportData(team) {
    let data = [];

    // Class info
    data.push([`${team.get('label')}`]);

    // Column headers
    let headers = ['Last Name', 'First Name', 'Year', 'Medical', 'Permission'];

    if (this.weekly) {
      let periods = ['', '', '', ''];

      this.dates.currentTimetable.days.forEach((day) => {
        headers.push(titleize(day.name));

        day.periods.forEach((period, i) => {
          if (i > 0) {
            headers.push('');
          }

          periods.push(period.shortName);
        });
      });

      data.push(headers, periods);
    } else {
      let timetableDay = this.dates.dayForDate(this.date);

      if (!isEmpty(timetableDay)) {
        timetableDay.periods.forEach((period) => {
          headers.push(this.reliever ? period.shortName : period.longName);
        });
      }

      if (this.reliever) {
        headers.push('Medical Notes');
        headers.push('Comment');
      }

      data.push(headers);
    }

    // Data
    // Sort data
    let sortedLearners = sortBy(
      this.learnerTeamMap[team.get('value')],
      `person.${this.sortProp}`,
    );
    sortedLearners =
      this.sortDir === 'asc' ? sortedLearners : sortedLearners.reverse();
    sortedLearners.forEach((learner) => {
      let permission = !!learner.person.get(
        'fields.core:permissionAlert.value',
      );
      let medical =
        !!learner.person.get('fields.core:criticalInfo.value') ||
        !!learner.person.get('fields.core:medicalCondition.value') ||
        !!learner.person.get('fields.core:allergies.value') ||
        !!learner.person.get('fields.custom:medicalNotes.value') ||
        learner.person.get('learner.currentMedicalConditions').length;

      if (EnrolmentStatus.CURRENT === learner.person.enrolmentStatus) {
        let row = [
          `${learner.person.lastName}`,
          `${learner.person.firstName}`,
          `${learner.person.currentYearLevel}`,
          medical ? '**' : '',
          permission ? '***' : '',
        ];

        if (!this.weekly) {
          let timetableDay = this.dates.dayForDate(this.date);

          if (!isEmpty(timetableDay)) {
            timetableDay.periods.forEach(() => {
              row.push('');
            });
          }

          if (this.reliever) {
            row.push(learner.person.get('fields.core:medicalNotes.value'));
          }
        }

        data.push(row);
      }
    });

    return data;
  }
}
